import React, { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { ingestFile } from '../../orbit/lib/intakeApi';
import { plural } from './roomCopy';

/*
 * Dropping a document, with nothing on the page to say so.
 *
 * The buttons that want this already exist and already say what they do:
 * "Upload docs" on a department card, "Drop an org chart" under the list. They
 * used to open a drawer that slid over the very departments you were reading in
 * order to decide what to drop, then a panel that sat above them saying the
 * same thing the buttons said. Neither earned its space. This is the picker
 * and the read behind those buttons, and it draws nothing.
 *
 * Writing a note instead of dropping a file still lives on the Add context
 * drawer, which every open question opens from its own "From a doc" button.
 */

export interface ContextPickerHandle {
  pick: () => void;
}

const errorText = (err: unknown, fallback: string): string => {
  const e = err as { response?: { data?: { detail?: string } } };
  return e?.response?.data?.detail || fallback;
};

export const ContextPicker = forwardRef<ContextPickerHandle, {
  clientId: string;
  onDone: () => void;
}>(({ clientId, onDone }, ref) => {
  const picker = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);

  useImperativeHandle(ref, () => ({ pick: () => picker.current?.click() }), []);

  /* Reading takes a few seconds server-side and there is no panel to draw a bar
     in, so the toast carries the whole story: one when it starts, one with the
     count when it lands. */
  const drop = async (file: File | null) => {
    if (!file || busy) return;
    setBusy(true);
    const reading = toast.loading(`Reading ${file.name}`);
    try {
      const res = await ingestFile('doc', file, file.name, clientId);
      const facts = (res as unknown as { deltas?: unknown[] }).deltas?.length || 0;
      toast.update(reading, {
        render: `${file.name} read. ${plural(facts, 'fact', 'facts')} updated; departments and people it names wait to be confirmed.`,
        type: 'success',
        isLoading: false,
        autoClose: 5000,
      });
      onDone();
    } catch (err) {
      toast.update(reading, {
        render: errorText(err, `Could not read ${file.name}.`),
        type: 'error',
        isLoading: false,
        autoClose: 5000,
      });
    } finally {
      setBusy(false);
      if (picker.current) picker.current.value = '';
    }
  };

  return (
    <input
      ref={picker}
      type="file"
      className="hidden"
      disabled={busy}
      onChange={(e) => drop(e.target.files && e.target.files[0])}
    />
  );
});

ContextPicker.displayName = 'ContextPicker';

export default ContextPicker;
